import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/sr';
import { passkitAPI } from '../../services/api';
import { logger } from '../../utils/logger';

interface Venue {
  id: string;
  name: string;
  address?: string;
}

interface AddEventDialogProps {
  open: boolean;
  onClose: () => void;
  productionId: string;
  preselectedDate?: Dayjs | null;
  onSuccess: () => void;
}

const AddEventDialog: React.FC<AddEventDialogProps> = ({
  open,
  onClose,
  productionId,
  preselectedDate,
  onSuccess
}) => {
  const [eventDate, setEventDate] = useState<Dayjs | null>(preselectedDate || dayjs());
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loadingVenues, setLoadingVenues] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load venues on mount
  useEffect(() => {
    if (open) {
      loadVenues();
    }
  }, [open]);

  // Update date if preselected date changes
  useEffect(() => {
    if (preselectedDate) {
      setEventDate(preselectedDate);
    }
  }, [preselectedDate]);

  const loadVenues = async () => {
    try {
      setLoadingVenues(true);
      const response = await passkitAPI.listVenues();

      if (response.venues) {
        setVenues(response.venues);
      }
    } catch (err: any) {
      logger.error('Error loading venues:', err);
      setError('Greška pri učitavanju venue-a');
    } finally {
      setLoadingVenues(false);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!eventDate) {
      setError('Morate izabrati datum i vreme događaja');
      return;
    }

    if (!selectedVenue) {
      setError('Morate izabrati Venue');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call API to create event for production
      // Note: Event is NOT tied to specific ticket type
      const response = await passkitAPI.createEvent({
        productionId,
        scheduledStartDate: eventDate.toISOString(),
        venueId: selectedVenue.id
      });

      logger.log('✅ Event created successfully:', response);

      onSuccess();
      handleClose();
    } catch (err: any) {
      logger.error('Error adding event:', err);
      setError(err.response?.data?.detail || 'Greška pri dodavanju događaja');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEventDate(dayjs());
    setSelectedVenue(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Dodaj Novi Događaj
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Event Date/Time Picker */}
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sr">
            <DateTimePicker
              label="Datum i Vreme Događaja *"
              value={eventDate}
              onChange={(newValue) => {
                // Handle both Dayjs and Date types, set seconds to 00
                if (newValue) {
                  const dateWithoutSeconds = dayjs(newValue).second(0).millisecond(0);
                  setEventDate(dateWithoutSeconds);
                } else {
                  setEventDate(null);
                }
              }}
              disabled={loading}
              format="DD.MM.YYYY HH:mm"
              views={['year', 'month', 'day', 'hours', 'minutes']}
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: { mb: 2 }
                }
              }}
            />
          </LocalizationProvider>

          {/* Venue Selector */}
          <Autocomplete
            options={venues}
            getOptionLabel={(option) => option.name}
            value={selectedVenue}
            onChange={(event, newValue) => setSelectedVenue(newValue)}
            loading={loadingVenues}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Venue *"
                helperText="Izaberite postojeći venue ili kreirajte novi u Venues sekciji"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingVenues ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={loading}
        >
          Otkaži
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !eventDate || !selectedVenue}
        >
          {loading ? <CircularProgress size={24} /> : 'Dodaj Događaj'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEventDialog;
